import type { ReactNode } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

import type { RequestError } from '@/renderer/utils/errors';
import type { UseRequestResult } from '@/renderer/utils/useRequest';

import { friendlyRequestError } from './friendlyErrors';

export default FormErrors;

type WrapProps<
  F extends FieldValues,
  R extends FieldValues = F,
  IgnoreMismatch extends boolean = false,
> = {
  form: UseFormReturn<F>;
  ignoreMismatch?: IgnoreMismatch;
  // @TODO biome-ignore lint/suspicious/noExplicitAny:
  request?: UseRequestResult<any, IgnoreMismatch extends true ? R : F>;
  rewrite?: (err: RequestError) => ReactNode;
};

function FormErrors<
  F extends FieldValues = FieldValues,
  R extends FieldValues = F,
  IgnoreMismatch extends boolean = false,
>({ form, request, rewrite }: WrapProps<F, R, IgnoreMismatch>) {
  const fieldKeys = form.control._fields ? Object.keys(form.control._fields) : null;
  const errors = form.formState.errors;

  // Field-level problems are shown under their inputs, so the summary only
  // carries what is not already visible there, in member-facing copy (#2142).
  // A `rewrite` owns the whole request message, so it is never doubled here.
  const requestMessage =
    !!request?.error && !rewrite ? friendlyRequestError(request.error, fieldKeys || []) : null;

  return (
    <>
      {/* Misc errors from the formState that aren't tied specifically to a field */}
      {Object.keys(errors).map((key) => {
        if (!fieldKeys || !fieldKeys.includes(key)) {
          /* Only show a generic request error if it isn't the same as the formState Error */
          // @TODO biome-ignore lint/suspicious/noExplicitAny:
          if ((request?.error as any)?.message !== errors[key as keyof F]?.message) {
            const text = friendlyRequestError(errors[key as keyof F]?.message?.toString());

            return text ? (
              <div key={key}>
                <label>{text}</label>
              </div>
            ) : null;
          }
        }
      })}

      {!!request?.error && !!rewrite && (
        <div className="space-x-2 text-red-500">
          {/* @TODO biome-ignore lint/suspicious/noExplicitAny: */}
          <label className="text-red-400">{rewrite(request.error as any)}</label>
        </div>
      )}

      {!!requestMessage && (
        <div className="space-x-2 text-red-500">
          <label className="text-red-400">{requestMessage}</label>
        </div>
      )}
    </>
  );
}
